/**
 * GraphsContainer Component
 * Main container for the Graphs tab functionality
 * Orchestrates chart view, track points list, and column configuration
 *
 * State Management:
 * - Time range is stored in local state (not Redux) per Requirements 5.3
 * - Supports controlled mode via initialTimeRange prop for state persistence across tab switches (Requirements 5.4)
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { ChartView } from './ChartView';
import { GraphsTrackPointsList } from './GraphsTrackPointsList';
import { GraphsColumnConfig } from './GraphsColumnConfig';
import { GraphsTimeRangePicker } from './GraphsTimeRangePicker';
import {
  DEFAULT_SELECTED_FIELDS,
  DEFAULT_NUMERIC_FIELDS,
} from './constants';
import {
  getDefault24HourRange,
  transformTrackPointsToChartData,
  findTrackPointIndex,
} from './utils';
import { useGetVehicleRoutingQuery, useGetFormattedVehicleRouteQuery } from '../services';
import { QISpinner } from '../../../components';
import { NoData } from '../../Shared';
import { SelectedAssetSummary } from '../Shared';
import moment from 'moment';
import { generateDummyTrackPoints, shouldUseDummyData, getDummyDataMessage } from './dummyData';
import './graphs.scss';

/**
 * @param {Object} props
 * @param {string} props.deviceId - Device identifier
 * @param {string} props.selectedVehiclePlate - Selected vehicle plate number
 * @param {string} props.type - 'Devices' or 'Vehicles'
 * @param {Object} props.initialTimeRange - Optional initial time range for state persistence (Requirements 5.4)
 * @param {Function} props.onTimeRangeChange - Optional callback when time range changes (for parent state sync)
 */
export const GraphsContainer = ({
  deviceId,
  selectedVehiclePlate,
  type,
  initialTimeRange,
  onTimeRangeChange,
}) => {
  // Local state for time range (isolated from global state per Requirements 5.3)
  // Initialize with provided initialTimeRange or default 24-hour range (Requirements 5.1)
  const [timeRange, setTimeRange] = useState(() => {
    if (initialTimeRange && initialTimeRange.startDate && initialTimeRange.endDate) {
      return {
        startDate: moment(initialTimeRange.startDate),
        endDate: moment(initialTimeRange.endDate),
      };
    }
    return getDefault24HourRange();
  });

  // Track if this is the first render to avoid unnecessary callbacks
  const isFirstRender = useRef(true);

  // Notify parent of time range changes for state persistence (Requirements 5.4)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (onTimeRangeChange) {
      onTimeRangeChange(timeRange);
    }
  }, [timeRange, onTimeRangeChange]);

  // Selected fields for chart display
  const [selectedFields, setSelectedFields] = useState(DEFAULT_SELECTED_FIELDS);

  // Selected track point for highlighting
  const [selectedTrackPoint, setSelectedTrackPoint] = useState(null);

  // Column config visibility
  const [showColumnConfig, setShowColumnConfig] = useState(false);

  // Dummy data mode
  const useDummyData = shouldUseDummyData();
  const [showDummyDataBanner, setShowDummyDataBanner] = useState(useDummyData);

  // Fetch data for Devices type
  // Note: API maximum page size is 200
  const {
    data: devicesResponse,
    isFetching: devicesIsFetching,
    isSuccess: devicesIsSuccess,
  } = useGetVehicleRoutingQuery(
    {
      deviceId,
      start_time: moment(timeRange.startDate).valueOf(),
      end_time: moment(timeRange.endDate).valueOf(),
      per_page: 200,
      page: 0,
      isTime: true,
      noFields: true,
      plate_number: selectedVehiclePlate,
      type: type,
      source_id: [deviceId],
      // Graphs plot a chronological time series, so they keep ascending route
      // order (only the live summary loads the newest page first).
      sortOrder: "ASC",
    },
    { skip: type !== 'Devices' || !deviceId }
  );

  // Fetch data for Vehicles type
  // Note: API maximum page size is 200
  const {
    data: vehiclesResponse,
    isFetching: vehiclesIsFetching,
    isSuccess: vehiclesIsSuccess,
  } = useGetFormattedVehicleRouteQuery(
    {
      plate_number: selectedVehiclePlate,
      start_time: moment(timeRange.startDate).valueOf(),
      end_time: moment(timeRange.endDate).valueOf(),
      per_page: 200,
      page: 0,
      isTime: true,
      // Graphs keep ascending route order.
      sortOrder: "ASC",
    },
    { skip: type !== 'Vehicles' || !selectedVehiclePlate }
  );

  // Determine which response to use based on type
  const response = type === 'Devices' ? devicesResponse : vehiclesResponse;
  const isFetching = type === 'Devices' ? devicesIsFetching : vehiclesIsFetching;
  const isSuccess = type === 'Devices' ? devicesIsSuccess : vehiclesIsSuccess;

  // Track points data - use dummy data if enabled and no real data
  const trackPoints = useMemo(() => {
    const realData = response?.data || [];

    // Use dummy data if enabled and no real data is available
    if (useDummyData && realData.length === 0) {
      return generateDummyTrackPoints(50, timeRange);
    }

    return realData;
  }, [response, useDummyData, timeRange]);

  // Transform data for chart
  const chartData = useMemo(
    () => transformTrackPointsToChartData(trackPoints, selectedFields, DEFAULT_NUMERIC_FIELDS),
    [trackPoints, selectedFields]
  );

  // Find highlighted point index
  const highlightedIndex = useMemo(
    () => findTrackPointIndex(trackPoints, selectedTrackPoint),
    [trackPoints, selectedTrackPoint]
  );

  // Handle track point selection
  // The GraphsTrackPointsList component handles toggle logic and passes null for deselection
  const handleTrackPointSelect = useCallback((point) => {
    setSelectedTrackPoint(point);
  }, []);

  // Handle time range change
  const handleTimeRangeChange = useCallback((newRange) => {
    setTimeRange(newRange);
    setSelectedTrackPoint(null); // Clear selection on time range change
  }, []);

  // Handle field selection change
  const handleFieldsChange = useCallback((fields) => {
    setSelectedFields(fields);
  }, []);

  // Loading state
  if (isFetching) {
    return (
      <div className="graphs-container graphs-container--loading">
        <QISpinner size="50px" />
      </div>
    );
  }

  // No data state
  if (isSuccess && trackPoints.length === 0) {
    return (
      <div className="graphs-container graphs-container--empty">
        <div className="graphs-header">
          {selectedVehiclePlate && (
            <SelectedAssetSummary
              plateNumber={selectedVehiclePlate}
              cardType={type === "Devices" ? "Devices" : "Vehicles"}
              variant="plate"
            />
          )}
          <GraphsTimeRangePicker
            selectedRange={timeRange}
            onRangeChange={handleTimeRangeChange}
          />
        </div>
        <div className="graphs-empty-content">
          <NoData />
        </div>
      </div>
    );
  }

  return (
    <div className="graphs-container">
      {/* Dummy data banner */}
      {useDummyData && showDummyDataBanner && trackPoints.length > 0 && (
        <div className="graphs-dummy-data-banner">
          <span>{getDummyDataMessage()}</span>
          <button
            className="graphs-dummy-data-banner__close"
            onClick={() => setShowDummyDataBanner(false)}
            title="Close"
          >
            ×
          </button>
        </div>
      )}

      {/* Header with time range picker and column config */}
      <div className="graphs-header">
        {selectedVehiclePlate && (
          <SelectedAssetSummary
            plateNumber={selectedVehiclePlate}
            cardType={type === "Devices" ? "Devices" : "Vehicles"}
            variant="plate"
          />
        )}
        <GraphsTimeRangePicker
          selectedRange={timeRange}
          onRangeChange={handleTimeRangeChange}
        />
        <div className="graphs-config-wrapper">
          <button
            className="graphs-config-btn"
            onClick={() => setShowColumnConfig(!showColumnConfig)}
            title="Configure fields"
          >
            <span className="icon-settings">⚙</span>
          </button>
          {/* Column configuration panel */}
          {showColumnConfig && (
            <GraphsColumnConfig
              availableFields={DEFAULT_NUMERIC_FIELDS}
              selectedFields={selectedFields}
              onFieldsChange={handleFieldsChange}
              onClose={() => setShowColumnConfig(false)}
            />
          )}
        </div>
      </div>

      {/* Chart section - top 50% */}
      <div className="graphs-chart-section">
        <ChartView
          data={chartData}
          selectedFields={selectedFields}
          highlightedIndex={highlightedIndex}
          trackPoints={trackPoints}
        />
      </div>

      {/* Track points list section - bottom 50% */}
      <div className="graphs-list-section">
        <GraphsTrackPointsList
          data={trackPoints}
          selectedPoint={selectedTrackPoint}
          onRowSelect={handleTrackPointSelect}
          selectedColumns={['source_time', ...selectedFields]}
        />
      </div>
    </div>
  );
};

export default GraphsContainer;
